/**
 * Shared write-gate for non-human DSL callers.
 *
 * DSL functions listed here may never be invoked by an automated caller.
 * Order placement must go through the native `place_order` path (validation,
 * MaxOrderVolume cap, consent dialog) — these DSL calls would send live market
 * orders with none of those guards.
 *
 * The terminal command line keeps them: a human typing `Orders.Buy(...)` is
 * making the decision themselves. Every machine caller — the Vybe Agent and
 * the desktop MCP bridge — shares this one definition so the two paths cannot
 * drift apart.
 */

/** Matches a DSL source line that places an order. */
export const AGENT_BLOCKED_DSL = /^\s*Orders\.(Buy|Sell|Market)\s*\(/;

/** The same set as dotted catalog paths, for filtering a published catalog. */
export const AGENT_BLOCKED_CATALOG_PATHS: ReadonlySet<string> = new Set([
  "Orders.Buy",
  "Orders.Sell",
  "Orders.Market",
]);

/** Refusal text handed back to the caller when the guard trips. */
export const AGENT_BLOCKED_DSL_MESSAGE =
  "Order placement via DSL is not allowed for automated callers. Use the place_order tool " +
  "(requires user trading consent); if place_order is not in your tool list, present " +
  "the trade plan and ask the user to enable agent trading consent in Settings.";

/** True when this DSL source line places an order. */
export function isBlockedDslWrite(call: string): boolean {
  return AGENT_BLOCKED_DSL.test(call);
}

/**
 * Every DSL call that mutates live trading state.
 *
 * `AGENT_BLOCKED_DSL` above names three functions explicitly, which leaves
 * real money exposed: `Orders.Send` is a full OrderSend, and
 * `Orders.Close` / `CloseBy` / `Cancel` / `Modify` / `ModifyPrice` all change
 * live positions — none of them match that pattern. Worse, the list is
 * open-ended: a new `Orders.*` function would be silently callable.
 *
 * The MCP bridge therefore gates on the whole `Orders` namespace rather than
 * an enumeration, so anything added later is denied by default rather than
 * exposed by default. The agent keeps its narrower, longer-standing rule —
 * broadening that would remove capabilities it ships with today.
 */
export const TRADING_MUTATION_DSL = /^\s*Orders\s*\./;

/** True when this DSL source line mutates live trading state in any way. */
export function isTradingMutation(call: string): boolean {
  return TRADING_MUTATION_DSL.test(call);
}

/** Refusal text for a blocked trading mutation over the MCP bridge. */
export const TRADING_MUTATION_MESSAGE =
  "Trading writes are disabled for this MCP bridge. Every Orders.* call is blocked " +
  "because it would mutate live positions. Enable 'Allow trading writes' in " +
  "Settings → MCP Server if you intend to let connected tools trade.";
